import React, { memo, ReactNode } from 'react';

import { AutoComplete, Input, Select } from 'antd';

import SimpleList from '@plasma/ui.display.simple-list';
import Tooltip from '@plasma/ui.display.tooltip';
import Button from '@plasma/ui.general.button';
import { RadioGroup } from '@plasma/ui.input.radio-group';
import Modal from '@plasma/ui.layout.modal';
import ThemeProvider from '@plasma/ui.themes.theme-provider';
import { ThemeOverride } from '@plasma/ui.utils.types';

import LoadingComponent from '../../components/Loding-component/LoadingComponent';
import LoadingComponentScalable from '../../components/Loding-component/LoadingComponentMini';
import TopBarThemed from '../../components/top-bar-themed';
import useConsumableOverviewHook from './consumable-overview.hook';
import { StyledSpan, NormalPlasmaButton } from './consumable-overview.styles';
import * as styled from './consumable-overview.styles';
import themes from './themes';

export interface ConsumableOverviewProps {
  children?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

type Props = ConsumableOverviewProps & ThemeOverride;

function ConsumableOverview(props: Props) {
  const { children, className, style, size, themeMode } = props;

  const {
    t,
    searchQuery,
    setSearchQuery,
    searchSuggestions,
    prod1,
    handleProd1Change,
    prod2,
    handleProd2Change,
    prod1Dimension,
    handleProd1DimChange,
    handleProd2DimeChange,
    prod2Dimension,
    prod1ProdType,
    prod2ProdType,
    prod1Desc,
    prod2Desc,
    dataForColumns,
    tableColumns,
    cartItems,
    showModal,
    showCodeModal,
    modalTitle,
    iFrameSrc,
    guidByProd,
    guidByProdArray,
    pageNumber,
    loadingFile,
    handleAddToCart,
    handleRemoveFromCart,
    handleClear,
    handleGoToCart,
    handleRollPassForProduct,
    HandleModalCancel,
    HandleCodeModalCancel,
    getCartItemsOfConsumable,
    handleNextPage,
    handlePreviousPage,
    handleConsumableCodeClick,
    handlePdfOfChangeover,
    handleDrawingModal,
    handleDrawingModalCancel,
    handleCancelChangeoverModal,
    handleEquipmentClick,
    viewDrawing,
    differentCosumableCodeArray,
    codeModalTitle,
    usedProfilesForCode,
    usedEquipmentsForCode,
    descriptionForCode,
    showChangeOverModal,
    iFrameSrcForChangeover,
    selectedEquipment,
    optionsLoading,
    waitingForFile,
    allGuidData,
    selectedCustomerConsumableCodeDetails,
    allGuidDataForEquipment,
    handleFilterChange,
    filterMode,
    setFilterMode,
  } = useConsumableOverviewHook(props);

  const totalItems = cartItems.reduce((total, currentItem) => {
    return total + currentItem.itemcount;
  }, 0);

  let serialNumber = 0;
  const UserRoles = JSON.parse(sessionStorage.getItem('UserRoles') || '');
  const isAdmin = sessionStorage.getItem('superUserRole');

  // --- Visibility Logic Helpers ---
  const checkIsSpecialValue = (val: any) => !val || val === 'N/A' || val === 'DUMMY' || val === 'NA' || val === '-';

  // Get active dynamic columns
  const activeColumns = tableColumns ? tableColumns.filter(Boolean) : [];
  const hasMultipleCols = activeColumns.length > 1;

  return (
    <ThemeProvider themes={themes} selectedSize={size} selectedMode={themeMode}>
      {!(dataForColumns && dataForColumns.length) && <LoadingComponent />}
      <styled.ConsumableOverview className={`consumable-overview ${className ?? ''}`.trim()} style={style}>
        <TopBarThemed topHeading={t('consumable-overview-and-utilization')} />
        {loadingFile && <LoadingComponent />}

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ marginTop: '4vh', width: '76%', overflowX: 'hidden', overflowY: 'auto', maxHeight: '83vh' }}>
            {/* --- FILTER BUTTON STRIP --- */}
            {dataForColumns && dataForColumns.length > 0 && (
              <div
                style={{
                  marginBottom: '15px',
                  padding: '10px',
                  backgroundColor: '#a8a8a899',
                  borderRadius: '5px',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <RadioGroup
                  direction="horizontal"
                  buttonStyle="solid"
                  style={{ gap: '8px' }}
                  value={filterMode as any}
                  onChange={handleFilterChange}
                  options={[
                    { label: t('hide_unused') || 'Hide Unused', value: 'hide_unused' },
                    { label: t('show_all') || 'Show All', value: 'show_all' },
                    { label: t('show_differences') || 'Only Differences', value: 'show_differences' },
                    { label: t('show_same') || 'Only Similar', value: 'show_same' },
                    { label: t('profile_dependent') || 'Profile Dependent', value: 'profile_dependent' },
                    { label: t('profile_independent') || 'Profile Independent', value: 'profile_independent' },
                    { label: t('physical_item') || 'Physical Item', value: 'physical_item' },
                    { label: t('information_only') || 'Information Only', value: 'information_only' },
                  ]}
                />

                {/* Suggestions: the areas, zones and consumables on the screen that match what's typed */}
                <AutoComplete
                  value={searchQuery}
                  options={searchSuggestions}
                  onChange={(value: string) => setSearchQuery(value ?? '')}
                  style={{ width: '16vw' }}
                  listHeight={320}
                >
                  <Input.Search
                    placeholder={'Search by Area, Zone or Consumable'}
                    title={'Search by Area, Zone or Consumable'}
                    allowClear
                  />
                </AutoComplete>
              </div>
            )}

            {dataForColumns && dataForColumns.length > 0 && (
              <styled.Table>
                <styled.TableHeader>
                  <styled.TableHeadingRow>
                    <styled.TableHeadingCell>Sl. No.</styled.TableHeadingCell>
                    <styled.TableHeadingCell>Area</styled.TableHeadingCell>
                    <styled.TableHeadingCell>Zone</styled.TableHeadingCell>
                    <styled.TableHeadingCell>Equipment</styled.TableHeadingCell>
                    <styled.TableHeadingCell>Quantity</styled.TableHeadingCell>
                    <styled.TableHeadingCell>Consumable</styled.TableHeadingCell>
                    {tableColumns.map(
                      (column, index) =>
                        column && (
                          <styled.TableHeadingCell key={index}>{column?.split(':')[1].trim()}</styled.TableHeadingCell>
                        ),
                    )}
                  </styled.TableHeadingRow>
                </styled.TableHeader>
                <tbody>
                  {dataForColumns?.map((row, index) => {
                    // --- ROW FILTERING LOGIC ---
                    const isUnused =
                      activeColumns.length > 0 && activeColumns.every((col) => checkIsSpecialValue(row[col]));
                    const isSame =
                      hasMultipleCols && !isUnused && activeColumns.every((col) => row[col] === row[activeColumns[0]]);
                    const isDiff = hasMultipleCols && !isUnused && !isSame;
                    const isProfileIndependent = row.consumables.some((c: any) => c.profileDependecy === 'A');
                    const isProfileDependent = row.consumables.some((c: any) => c.profileDependecy === 'B');
                    const isPhysicalItem = row.consumables.some((c: any) => c.consumableCategory === 'p');
                    const isInformationOnly = row.consumables.some((c: any) => c.consumableCategory === 'i');

                    if (filterMode === 'hide_unused' && isUnused) return null;
                    if (filterMode === 'show_same' && !isSame) return null;
                    if (filterMode === 'show_differences' && !isDiff) return null;
                    if (filterMode === 'profile_independent' && !isProfileIndependent) return null;
                    if (filterMode === 'profile_dependent' && !isProfileDependent) return null;
                    if (filterMode === 'physical_item' && !isPhysicalItem) return null;
                    if (filterMode === 'information_only' && !isInformationOnly) return null;
                    // 'show_all' bypasses all early returns

                    // ---  Search Query Filter (Area, Zone, Consumables) ---
                    const query = searchQuery?.toLowerCase().trim() || '';
                    if (query) {
                      const matchesArea = row.areacode?.toLowerCase().includes(query);
                      const matchesZone = row.zonecode?.toLowerCase().includes(query);
                      const matchesConsumable = row.Consumable?.toLowerCase().includes(query);

                      if (!matchesArea && !matchesZone && !matchesConsumable) {
                        return null; // Skip rendering if it doesn't match search
                      }
                    }

                    // Increment serial number only if row is rendered
                    serialNumber += 1;
                    const isDocGuidAvailable = allGuidDataForEquipment?.some(
                      (item: any) => item.docfor === row.drawingnumber,
                    );

                    return (
                      <styled.TableRow key={index} isLight={(serialNumber - 1) % 2 === 0}>
                        <styled.TableCell>{serialNumber}</styled.TableCell>
                        <styled.TableCell>{row.areacode}</styled.TableCell>
                        <styled.TableCell>{row.zonecode}</styled.TableCell>
                        <styled.TableCell>
                          <styled.LinkCell
                            onClick={() => handleEquipmentClick(row.drawingnumber)}
                            isDrawingAvailable={isDocGuidAvailable}
                          >
                            {row.equipmentcode}
                          </styled.LinkCell>
                        </styled.TableCell>
                        <styled.TableCell>{row.quantityInstalled}</styled.TableCell>
                        <styled.TableCell>{row.Consumable}</styled.TableCell>
                        {tableColumns.map((column, columnIndex) => {
                          if (column) {
                            const columnKey = column;
                            const cellValue = row[columnKey];
                            const itemsInCorrespondingCart = getCartItemsOfConsumable(
                              index,
                              columnKey.split(':')[0],
                              column?.split('-')[0],
                              column?.split('-')[1],
                              row[columnKey],
                              row.Consumable,
                              row.areacode,
                              row.zonecode,
                              row.equipmentcode,
                            );
                            const sizeIncrement = 20 + itemsInCorrespondingCart?.toString()?.length * 2;
                            const uniqueCode = differentCosumableCodeArray
                              ?.map((item) => item.consumableCode2)
                              ?.includes(row[column]);
                            const oneRowIndex = differentCosumableCodeArray
                              ?.map((item) => item.rowIndex)
                              ?.includes(index);
                            const isSpecialValue = checkIsSpecialValue(cellValue);

                            return (
                              <styled.TableCell
                                key={columnIndex}
                                backgroundColor={
                                  prod1 &&
                                  prod1Dimension &&
                                  prod2 &&
                                  prod2Dimension &&
                                  uniqueCode &&
                                  oneRowIndex &&
                                  columnIndex === 1
                                }
                              >
                                {cellValue && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    {isSpecialValue ? (
                                      <styled.SpecialValueContainer>{cellValue}</styled.SpecialValueContainer>
                                    ) : (
                                      <styled.LinkCell
                                        isDrawingAvailable
                                        onClick={() => handleConsumableCodeClick(index, cellValue)}
                                      >
                                        {cellValue}
                                      </styled.LinkCell>
                                    )}

                                    <styled.CartButtonsContainer>
                                      <styled.CartIndicator
                                        itemsInCart={itemsInCorrespondingCart}
                                        isSpecialValue={isSpecialValue}
                                        style={{ width: `${sizeIncrement}px` }}
                                      >
                                        {itemsInCorrespondingCart}
                                      </styled.CartIndicator>

                                      <div>
                                        <styled.CartButton
                                          htmlType="button"
                                          prefixIcon={{ name: 'keyboard_arrow_up' }}
                                          type="secondary"
                                          disabled={isSpecialValue}
                                          style={{ borderBottom: 'none' }}
                                          onClick={() => {
                                            handleAddToCart(
                                              index,
                                              column.split(':')[0],
                                              column.split('-')[0],
                                              column.split('-')[1],
                                              row[columnKey],
                                              row.Consumable,
                                              row.areacode,
                                              row.zonecode,
                                              row.drawingnumber,
                                            );
                                          }}
                                        />
                                        <styled.CartButton
                                          htmlType="button"
                                          prefixIcon={{ name: 'keyboard_arrow_down' }}
                                          type="secondary"
                                          disabled={isSpecialValue}
                                          style={{ borderTop: 'none' }}
                                          onClick={() =>
                                            handleRemoveFromCart(
                                              index,
                                              column.split(':')[0],
                                              column.split('-')[0],
                                              column.split('-')[1],
                                              row[columnKey],
                                              row.Consumable,
                                              row.areacode,
                                              row.zonecode,
                                              row.drawingnumber,
                                            )
                                          }
                                        />
                                      </div>
                                    </styled.CartButtonsContainer>
                                  </div>
                                )}
                              </styled.TableCell>
                            );
                          }
                        })}
                      </styled.TableRow>
                    );
                  })}
                </tbody>
              </styled.Table>
            )}
          </div>

          <div style={{ width: '30%', padding: '2vw' }}>
            <div style={{ borderRadius: '5px', backgroundColor: '#CCCCCC' }}>
              <div style={{ display: 'flex', alignItems: 'center', padding: '5px' }}>
                <StyledSpan> Profile 1: </StyledSpan>
                <Select
                  value={prod1}
                  onChange={handleProd1Change}
                  placeholder="type"
                  style={{ width: '180px', margin: '2%' }}
                  allowClear
                  disabled={optionsLoading}
                >
                  {prod1ProdType?.map((prod, index) => (
                    <option key={index} value={prod}>
                      {prod}
                    </option>
                  ))}
                </Select>
                <Select
                  disabled={!prod1 || optionsLoading}
                  value={prod1Dimension}
                  onChange={handleProd1DimChange}
                  style={{ width: '180px', margin: '2%' }}
                  placeholder="Dimension"
                  allowClear
                >
                  {prod1Desc?.map((prod1Dimension, index) => (
                    <option key={index} value={prod1Dimension}>
                      {prod1Dimension}
                    </option>
                  ))}
                </Select>
                {optionsLoading && <LoadingComponentScalable borderWidth={1} containerSize={40} initialSeconds={5} />}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', padding: '5px' }}>
                <StyledSpan> Profile 2: </StyledSpan>
                <Select
                  value={prod2}
                  onChange={handleProd2Change}
                  style={{ width: '180px', margin: '2%' }}
                  placeholder="type"
                  allowClear
                  disabled={optionsLoading}
                >
                  {prod2ProdType?.map((prod, index) => (
                    <option key={index} value={prod}>
                      {prod}
                    </option>
                  ))}
                </Select>
                <Select
                  value={prod2Dimension}
                  onChange={handleProd2DimeChange}
                  placeholder="Dimension"
                  style={{ width: '180px', margin: '2%' }}
                  disabled={!prod2 || optionsLoading}
                  allowClear
                >
                  {prod2Desc?.map((prod2Dimension, index) => (
                    <option key={index} value={prod2Dimension}>
                      {prod2Dimension}
                    </option>
                  ))}
                </Select>
                {optionsLoading && <LoadingComponentScalable borderWidth={1} containerSize={40} initialSeconds={5} />}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '2px',
                  backgroundColor: '#FFFFFF80',
                  margin: '4% 3% 0 3%',
                  borderRadius: '5px',
                }}
              >
                <NormalPlasmaButton
                  title={t('clear-selections')}
                  onClick={() => {
                    handleClear();
                  }}
                  style={{ margin: '10px' }}
                >
                  {t('clear-selections')}
                </NormalPlasmaButton>

                <styled.DifferenceContainer>
                  {t('total-differences')}{' '}
                  {prod1 && prod1Dimension && prod2 && prod2Dimension ? differentCosumableCodeArray.length : 0}
                </styled.DifferenceContainer>
              </div>
            </div>

            {prod1 && prod1Dimension && (
              <div
                style={{
                  borderRadius: '5px',
                  border: '1px solid black',
                  position: 'absolute',
                  top: '38vh',
                  left: '79vw',
                  padding: '1vh',
                  backgroundColor: '#CCCCCC99',
                }}
              >
                <NormalPlasmaButton
                  title={`${t('preview-roll-pass')} 1`}
                  onClick={() => {
                    handleRollPassForProduct(prod1);
                  }}
                  style={{ textWrap: 'wrap', margin: '15px', height: '15vh', width: '13vw' }}
                >
                  {`${t('preview-roll-pass')} 1`}
                </NormalPlasmaButton>
              </div>
            )}
            {prod2 && prod2Dimension && (
              <div
                style={{
                  borderRadius: '5px',
                  border: '1px solid black',
                  position: 'absolute',
                  top: '60vh',
                  left: '79vw',
                  padding: '1vh',
                  backgroundColor: '#CCCCCC99',
                }}
              >
                <NormalPlasmaButton
                  title={`${t('preview-roll-pass')} 2`}
                  onClick={() => {
                    handleRollPassForProduct(prod2);
                  }}
                  style={{ textWrap: 'wrap', margin: '15px', height: '15vh', width: '13vw' }}
                >
                  {`${t('preview-roll-pass')} 2`}
                </NormalPlasmaButton>
              </div>
            )}

            <Modal
              title={`${t('consumable')} ${codeModalTitle} ${t('main-data')}`}
              width={'max-content'}
              cancelText={t('cancel')}
              visible={showCodeModal}
              onCancel={() => HandleCodeModalCancel()}
              onOk={() => HandleCodeModalCancel()}
              footer={null}
            >
              <div style={{ display: 'flex', gap: '3vw' }}>
                {codeModalTitle && descriptionForCode && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                    <div
                      style={{
                        position: 'absolute',
                        top: '-15px', // Adjust this value to position the div correctly
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontWeight: 'bold',
                        fontSize: '15px',
                        border: '2px solid black',
                        padding: '5px 10px',
                        borderRadius: '5px',
                        backgroundColor: 'white', // Ensure it has a background
                        zIndex: '1', // Ensure it's above other elements
                      }}
                    >
                      {t('sms-codification')}
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        width: '38vw',
                        height: '50vh',
                        background: 'linear-gradient(to top left, #f0f0f5, #33adff34)',
                        padding: '15px 20px 20px 20px',
                        border: '2px solid black',
                        borderRadius: '5px',
                        position: 'relative',
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', marginTop: '30px' }}>
                          <div
                            style={{
                              width: '11vw',
                              fontWeight: 'bold',
                              marginRight: '52px',
                              fontSize: '15px',
                              border: '1px solid #75a3a3',
                              backgroundColor: '#f0f5f5',
                              padding: '5px 10px',
                              borderRadius: '5px',
                            }}
                          >
                            {t('sms-material-number')}:
                          </div>
                          <div
                            style={{
                              width: 'max-content',
                              marginRight: '5px',
                              padding: '5px',
                              backgroundColor: 'white',
                              borderRadius: '5px',
                              border: '1px solid #75a3a3',
                            }}
                          >
                            {codeModalTitle}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                          <div
                            style={{
                              width: '11vw',
                              fontWeight: 'bold',
                              marginRight: '52px',
                              fontSize: '15px',
                              border: '1px solid #75a3a3',
                              backgroundColor: '#f0f5f5',
                              padding: '5px 10px',
                              borderRadius: '5px',
                            }}
                          >
                            {t('description')}
                          </div>
                          <div
                            style={{
                              width: 'max-content',
                              marginRight: '5px',
                              padding: '5px',
                              backgroundColor: 'white',
                              borderRadius: '5px',
                              border: '1px solid #75a3a3',
                            }}
                          >
                            {descriptionForCode}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '20px' }}>
                          <div
                            style={{
                              width: '11vw',
                              fontWeight: 'bold',
                              marginRight: '40px',
                              fontSize: '15px',
                              border: '1px solid #75a3a3',
                              backgroundColor: '#f0f5f5',
                              padding: '5px 10px',
                              borderRadius: '5px',
                            }}
                          >
                            {t('used-in')}
                          </div>
                          <SimpleList
                            dataSource={usedEquipmentsForCode}
                            style={{
                              overflowX: 'auto',
                              overflowY: 'auto',
                              maxHeight: '14.5vh',
                              width: '21vw',
                              borderColor: '#8b8b8c',
                              borderRadius: '5px',
                            }}
                            renderItem={(item) => (
                              <div
                                style={{
                                  marginLeft: '10px',
                                  marginTop: '5px',
                                  display: 'flex',
                                  padding: '2px',
                                  alignItems: 'center',
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                  <div
                                    style={{
                                      width: '19vw',
                                      marginRight: '5px',
                                      padding: '2px',
                                      backgroundColor: 'white',
                                      borderRadius: '5px',
                                      border: '1px solid #75a3a3',
                                    }}
                                  >
                                    {item.equipmentcode} : {item.zonecode} : {item.areacode}
                                  </div>
                                </div>
                              </div>
                            )}
                            itemLayout="horizontal"
                          />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '20px' }}>
                          <div
                            style={{
                              width: '11vw',
                              fontWeight: 'bold',
                              marginRight: '40px',
                              fontSize: '15px',
                              border: '1px solid #75a3a3',
                              backgroundColor: '#f0f5f5',
                              padding: '5px 10px',
                              borderRadius: '5px',
                            }}
                          >
                            {t('used-for-profiles')}
                          </div>
                          <SimpleList
                            dataSource={usedProfilesForCode}
                            style={{
                              overflowX: 'auto',
                              overflowY: 'auto',
                              maxHeight: '14.5vh',
                              width: '16.5vw',
                              borderColor: '#8b8b8c',
                              borderRadius: '5px',
                            }}
                            renderItem={(item) => (
                              <div
                                style={{
                                  marginLeft: '10px',
                                  marginTop: '5px',
                                  display: 'flex',
                                  padding: '2px',
                                  alignItems: 'center',
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                  <div
                                    style={{
                                      width: '14vw',
                                      marginRight: '5px',
                                      padding: '2px',
                                      backgroundColor: 'white',
                                      borderRadius: '5px',
                                      border: '1px solid #75a3a3',
                                    }}
                                  >
                                    {item.producttype} : {item.productdescription}
                                  </div>
                                </div>
                              </div>
                            )}
                            itemLayout="horizontal"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {selectedCustomerConsumableCodeDetails ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                    <div
                      style={{
                        position: 'absolute',
                        top: '-15px', // Adjust this value to position the div correctly
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontWeight: 'bold',
                        fontSize: '15px',
                        border: '2px solid black',
                        padding: '5px 10px',
                        borderRadius: '5px',
                        backgroundColor: 'white', // Ensure it has a background
                        zIndex: '1', // Ensure it's above other elements
                      }}
                    >
                      {t('customer-codification')}
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        width: '38vw',
                        height: '50vh',
                        background: 'linear-gradient(to top left, #f0f0f5, #33adff34)',
                        padding: '15px 20px 20px 20px',
                        border: '2px solid black',
                        borderRadius: '5px',
                        position: 'relative',
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '30px', marginTop: '30px' }}>
                          <div
                            style={{
                              width: '13.5vw',
                              fontWeight: 'bold',
                              marginRight: '52px',
                              fontSize: '15px',
                              border: '1px solid #75a3a3',
                              backgroundColor: '#f0f5f5',
                              padding: '5px 10px',
                              borderRadius: '5px',
                            }}
                          >
                            {t('customer-material-number')}:
                          </div>
                          <div
                            style={{
                              width: 'max-content',
                              marginRight: '5px',
                              padding: '5px',
                              backgroundColor: 'white',
                              borderRadius: '5px',
                              border: '1px solid #75a3a3',
                            }}
                          >
                            {selectedCustomerConsumableCodeDetails !== undefined
                              ? selectedCustomerConsumableCodeDetails.megConsumableCodeExt
                              : 'NULL'}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '30px', marginTop: '20px' }}>
                          <div
                            style={{
                              width: '13.5vw',
                              fontWeight: 'bold',
                              marginRight: '52px',
                              fontSize: '15px',
                              border: '1px solid #75a3a3',
                              backgroundColor: '#f0f5f5',
                              padding: '5px 10px',
                              borderRadius: '5px',
                            }}
                          >
                            {t('description')}
                          </div>
                          <div
                            style={{
                              width: 'max-content',
                              marginRight: '5px',
                              padding: '5px',
                              backgroundColor: 'white',
                              borderRadius: '5px',
                              border: '1px solid #75a3a3',
                            }}
                          >
                            {selectedCustomerConsumableCodeDetails !== undefined
                              ? selectedCustomerConsumableCodeDetails.description
                              : 'NULL'}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '30px' }}>
                          <div
                            style={{
                              width: '13.5vw',
                              fontWeight: 'bold',
                              marginRight: '52px',
                              fontSize: '15px',
                              border: '1px solid #75a3a3',
                              backgroundColor: '#f0f5f5',
                              padding: '5px 10px',
                              borderRadius: '5px',
                            }}
                          >
                            {t('section-system-equipment')}:
                          </div>
                          <div
                            style={{
                              width: 'max-content',
                              marginRight: '5px',
                              padding: '5px',
                              backgroundColor: 'white',
                              borderRadius: '5px',
                              border: '1px solid #75a3a3',
                            }}
                          >
                            {selectedCustomerConsumableCodeDetails !== undefined
                              ? selectedCustomerConsumableCodeDetails.drawingNumber
                              : 'NULL'}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '30px' }}>
                          <div
                            style={{
                              width: '13.5vw',
                              fontWeight: 'bold',
                              marginRight: '52px',
                              fontSize: '15px',
                              border: '1px solid #75a3a3',
                              backgroundColor: '#f0f5f5',
                              padding: '5px 10px',
                              borderRadius: '5px',
                            }}
                          >
                            {t('warehouse-code')}:
                          </div>
                          <div
                            style={{
                              width: 'max-content',
                              marginRight: '5px',
                              padding: '5px',
                              backgroundColor: 'white',
                              borderRadius: '5px',
                              border: '1px solid #75a3a3',
                            }}
                          >
                            {selectedCustomerConsumableCodeDetails !== undefined
                              ? selectedCustomerConsumableCodeDetails.warehouseIdentifier
                              : 'NULL'}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '30px' }}>
                          <div
                            style={{
                              width: '13.5vw',
                              fontWeight: 'bold',
                              marginRight: '52px',
                              fontSize: '15px',
                              border: '1px solid #75a3a3',
                              backgroundColor: '#f0f5f5',
                              padding: '5px 10px',
                              borderRadius: '5px',
                            }}
                          >
                            {t('warehouse-position')}:
                          </div>
                          <div
                            style={{
                              width: 'max-content',
                              marginRight: '5px',
                              padding: '5px',
                              backgroundColor: 'white',
                              borderRadius: '5px',
                              border: '1px solid #75a3a3',
                            }}
                          >
                            {selectedCustomerConsumableCodeDetails !== undefined
                              ? selectedCustomerConsumableCodeDetails.warehousePosition
                              : 'NULL'}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '30px' }}>
                          <div
                            style={{
                              width: '13.5vw',
                              fontWeight: 'bold',
                              marginRight: '52px',
                              fontSize: '15px',
                              border: '1px solid #75a3a3',
                              backgroundColor: '#f0f5f5',
                              padding: '5px 10px',
                              borderRadius: '5px',
                            }}
                          >
                            {t('type-of-piece')}:
                          </div>
                          <div
                            style={{
                              width: 'max-content',
                              marginRight: '5px',
                              padding: '5px',
                              backgroundColor: 'white',
                              borderRadius: '5px',
                              border: '1px solid #75a3a3',
                            }}
                          >
                            {selectedCustomerConsumableCodeDetails !== undefined
                              ? selectedCustomerConsumableCodeDetails.inputMaterialId
                              : 'NULL'}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                          <div
                            style={{
                              width: '13.5vw',
                              fontWeight: 'bold',
                              marginRight: '52px',
                              fontSize: '15px',
                              border: '1px solid #75a3a3',
                              backgroundColor: '#f0f5f5',
                              padding: '5px 10px',
                              borderRadius: '5px',
                            }}
                          >
                            {t('notes')}:
                          </div>
                          <div
                            style={{
                              width: 'max-content',
                              marginRight: '5px',
                              padding: '5px',
                              backgroundColor: 'white',
                              borderRadius: '5px',
                              border: '1px solid #75a3a3',
                            }}
                          >
                            {selectedCustomerConsumableCodeDetails !== undefined
                              ? selectedCustomerConsumableCodeDetails.remarks
                              : 'NULL'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  ''
                )}
              </div>
              {allGuidData?.find((item: any) => item.docfor === codeModalTitle) ? (
                <div style={{ marginTop: '5px' }}>
                  <Button title={t('drawing')} style={{ borderRadius: '5px' }} onClick={handleDrawingModal} />
                </div>
              ) : (
                ''
              )}
            </Modal>

            <Modal
              title={
                selectedEquipment && selectedEquipment?.trim().length > 0
                  ? `${t('equipment')} : ${selectedEquipment}`
                  : `${t('consumable')} ${codeModalTitle}`
              }
              style={{}}
              width={'80%'}
              cancelText={t('cancel')}
              visible={viewDrawing}
              onCancel={() => handleDrawingModalCancel()}
              footer={null}
            >
              <iframe
                src={iFrameSrc}
                title="PDF"
                style={{ marginTop: '1vh', height: '70vh', width: '100%' }} // Set width to 100%
              />
            </Modal>

            <Modal
              title={modalTitle}
              style={{}}
              width={'80%'}
              cancelText={t('cancel')}
              visible={showModal}
              onCancel={() => HandleModalCancel()}
              onOk={() => HandleModalCancel()}
              footer={null}
            >
              <div style={{ display: 'flex', flexDirection: 'column', flex: '1', backgroundColor: '#cccccc99' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {guidByProd && (
                    <div
                      style={{
                        borderRight: '1px solid black',
                        borderLeft: '1px solid black',
                        borderRadius: '2px',
                        marginLeft: '45%',
                        backgroundColor: 'white',
                        color: '#0066cc',
                        fontWeight: 'bold',
                        fontSize: '1.5vh',
                        padding: '0 1vh 0 1vh',
                      }}
                    >
                      {t('page')}
                      {pageNumber} {t('of')} {guidByProdArray?.length}
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline' }}>
                    <Tooltip title={t('previous-page')} mouseEnterDelay={0.5}>
                      <Button
                        style={{ width: '2vw' }}
                        type="secondary"
                        prefixIcon={{ name: 'arrow_back_ios' }}
                        disabled={!!(pageNumber === 1 || guidByProd?.length === 1)}
                        onClick={handlePreviousPage}
                      />
                    </Tooltip>
                    <Tooltip title={t('next-page')} mouseEnterDelay={0.5}>
                      <Button
                        style={{ width: '2vw' }}
                        type="secondary"
                        prefixIcon={{ name: 'arrow_forward_ios' }}
                        disabled={!!(pageNumber === guidByProdArray?.length || guidByProdArray?.length === 1)}
                        onClick={handleNextPage}
                      />
                    </Tooltip>
                  </div>
                </div>
                <iframe
                  src={iFrameSrc}
                  title="PDF"
                  style={{ height: '66vh', width: '100%' }} // Set width to 100%
                />
              </div>
            </Modal>

            {((prod1 && prod1Dimension) || (prod2 && prod2Dimension)) && (
              <div
                style={{
                  borderRadius: '5px',
                  border: '',
                  position: 'absolute',
                  top: '83vh',
                  left: '79vw',
                  padding: '0.3vh',
                  backgroundColor: '#CCCCCC99',
                }}
              >
                <NormalPlasmaButton
                  title={`${t('goto-cart')}${totalItems} ${t('items-added')}`}
                  onClick={handleGoToCart}
                  suffixIcon={{ name: 'shopping_cart' }}
                  style={{ textWrap: 'wrap', margin: '15px', width: '13.7vw' }}
                  disabled={!isAdmin?.length && !UserRoles.includes('shopping-cart')}
                >
                  {`${t('goto-cart')} ${totalItems} ${t('items-added')}`}
                </NormalPlasmaButton>
              </div>
            )}

            {prod1 && prod1Dimension && prod2 && prod2Dimension ? (
              <div
                style={{
                  borderRadius: '5px',
                  border: '',
                  position: 'absolute',
                  top: '90vh',
                  left: '79vw',
                  padding: '0.3vh',
                  backgroundColor: '#CCCCCC99',
                }}
              >
                <NormalPlasmaButton
                  title={t('create-changeover-pdf')}
                  onClick={handlePdfOfChangeover}
                  style={{ margin: '15px', width: '13.7vw' }}
                >
                  {t('create-changeover-pdf')}
                </NormalPlasmaButton>
              </div>
            ) : (
              ''
            )}

            <Modal
              title={t('pdf-of-changeover')}
              style={{}}
              width={'80%'}
              okText="Ok"
              cancelText="Cancel"
              visible={showChangeOverModal}
              onCancel={() => handleCancelChangeoverModal()}
              onOk={() => handleCancelChangeoverModal()}
              footer={null}
            >
              {waitingForFile ? (
                <div style={{ marginTop: '1vh', height: '70vh', width: '100%' }}>
                  <LoadingComponent />
                </div>
              ) : (
                <iframe
                  src={iFrameSrcForChangeover}
                  title="PDF"
                  style={{ marginTop: '1vh', height: '70vh', width: '100%' }} // Set width to 100%
                />
              )}
            </Modal>
          </div>
        </div>
      </styled.ConsumableOverview>
    </ThemeProvider>
  );
}

export default memo(ConsumableOverview) as unknown as typeof ConsumableOverview;
